import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock } from "lucide-react";

/**
 * The one follow-up scheduler, shared by every page that books a next visit.
 * Pick a doctor and a date, then choose a free 15-minute slot off that doctor's
 * actual calendar — schedule, blocked dates, reservations at ALL their clinics,
 * and other patients' follow-ups all make a slot unavailable.
 */
export default function ScheduleVisitDialog({
  open,
  onOpenChange,
  patientId,
  patientName = "",
  initialDate = "",
  initialTime = "",
  initialDoctorId = "",
  onSaved,
}) {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(initialDoctorId);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [avail, setAvail] = useState(null);       // null = not loaded
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slotsNonce, setSlotsNonce] = useState(0); // bump to re-fetch after a 409

  useEffect(() => {
    if (!open) return;
    setDoctorId(initialDoctorId || "");
    setDate((initialDate || "").slice(0, 10));
    setTime(initialTime || "");
    api.get("/doctors")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setDoctors(list);
        // Sensible default: the only doctor, or the previously booked one.
        if (!initialDoctorId && list.length === 1) setDoctorId(list[0].id);
      })
      .catch(() => setDoctors([]));
    // eslint-disable-next-line
  }, [open]);

  // Refresh the slot grid whenever doctor or date changes.
  useEffect(() => {
    if (!open || !doctorId || !date) { setAvail(null); return; }
    let cancelled = false;
    setLoadingSlots(true);
    api.get(`/doctors/${doctorId}/followup-slots`, { params: { date, exclude_patient: patientId } })
      .then(({ data }) => { if (!cancelled) setAvail(data); })
      .catch(() => { if (!cancelled) setAvail(null); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, doctorId, date, slotsNonce]);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      await api.put(`/patients/${patientId}/next-visit`, clear
        ? { next_visit_date: "" }
        : { next_visit_date: date, next_visit_time: time || "", next_visit_doctor_id: doctorId || "" });
      toast.success(clear ? "Follow-up cleared" : `Scheduled ${date}${time ? ` at ${time}` : ""}`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not save");
      if (e?.response?.status === 409) {
        // Someone grabbed the slot first — drop the stale pick and show the
        // refreshed grid so it appears struck-through.
        setTime("");
        setSlotsNonce((n) => n + 1);
      }
    } finally {
      setSaving(false);
    }
  };

  const allSlots = avail ? [...(avail.slots || []), ...(avail.taken || [])].sort() : [];
  const takenSet = new Set(avail?.taken || []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="schedule-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock size={18} className="text-blue-700" /> Schedule next visit
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {patientName ? (
            <p className="text-sm text-slate-600">
              Set when <b>{patientName}</b> should return. Each slot is 15 minutes.
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Doctor</label>
              <select
                value={doctorId}
                onChange={(e) => { setDoctorId(e.target.value); setTime(""); }}
                className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm mt-1"
                data-testid="schedule-visit-doctor"
              >
                <option value="">Select doctor…</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setTime(""); }}
                className="mt-1"
                data-testid="schedule-visit-date"
              />
            </div>
          </div>

          {doctorId && date ? (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Time slot {time ? <span className="text-blue-700 normal-case font-bold">— {time}</span> : "(optional)"}
              </label>
              {loadingSlots ? (
                <div className="text-sm text-slate-500 mt-2">Checking the doctor's calendar…</div>
              ) : !avail ? (
                <div className="text-sm text-slate-500 mt-2">Could not load availability.</div>
              ) : !avail.active ? (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2 text-amber-900" data-testid="schedule-day-off">
                  The doctor is not available on this day.
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mt-2 max-h-56 overflow-auto" data-testid="slot-grid">
                  {allSlots.map((s) => {
                    const isTaken = takenSet.has(s);
                    const isSel = time === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={isTaken}
                        onClick={() => setTime(isSel ? "" : s)}
                        className={
                          "h-9 rounded-md text-xs font-medium border transition " +
                          (isSel
                            ? "bg-blue-600 border-blue-600 text-white"
                            : isTaken
                              ? "bg-slate-100 border-slate-200 text-slate-400 line-through cursor-not-allowed"
                              : "bg-white border-slate-300 text-slate-700 hover:border-blue-400")
                        }
                        data-testid={`slot-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-400">Pick a doctor and a date to see their free 15-minute slots.</div>
          )}
        </div>

        <DialogFooter className="mt-2">
          {initialDate ? (
            <Button variant="outline" className="text-red-600 mr-auto" onClick={() => save(true)} disabled={saving} data-testid="schedule-visit-clear">
              Clear follow-up
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => save(false)}
            disabled={saving || !date}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="schedule-visit-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
